import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const EXCHANGE_COLORS = {
  BINANCE_FUTURES: '#F0B90B',
  BYBIT: '#F7A600',
  OKX: '#FFFFFF',
  BITGET: '#00E6A0',
  DERIBIT: '#38BDF8',
  BITMEX: '#EF4444',
};

function formatExchangeLabel(name) {
  if (!name) return '?';
  const short = name
    .replace(/_FUTURES$/i, '')
    .replace(/_PERP$/i, '')
    .replace(/_SWAP$/i, '');
  return short.charAt(0).toUpperCase() + short.slice(1).toLowerCase();
}

function TradesPerMinPanel({ tradeRates }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const maxDim = 8192;
    canvas.width = Math.min(Math.round(rect.width * dpr), maxDim);
    canvas.height = Math.min(Math.round(rect.height * dpr), maxDim);
    ctx.scale(canvas.width / rect.width, canvas.height / rect.height);

    const w = rect.width;
    const h = rect.height;

    // Clear
    ctx.clearRect(0, 0, w, h);

    if (!tradeRates || Object.keys(tradeRates).length === 0) {
      ctx.fillStyle = '#333';
      ctx.font = '11px "DM Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(t('stats.noData'), w / 2, h / 2);
      return;
    }

    const exchanges = Object.keys(tradeRates);
    const padding = { top: 8, right: 8, bottom: 20, left: 32 };
    const chartW = w - padding.left - padding.right;
    const chartH = h - padding.top - padding.bottom;

    // Find max count across all exchanges for Y scale
    let maxCount = 0;
    let maxPoints = 0;
    for (const ex of exchanges) {
      const points = tradeRates[ex];
      if (points.length > maxPoints) maxPoints = points.length;
      for (const p of points) {
        if (p.count > maxCount) maxCount = p.count;
      }
    }
    if (maxCount === 0) maxCount = 1;

    // Draw grid lines
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 1;
    const gridLines = 4;
    for (let i = 0; i <= gridLines; i++) {
      const y = padding.top + (chartH / gridLines) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(w - padding.right, y);
      ctx.stroke();
    }

    // Y-axis labels
    ctx.fillStyle = '#555';
    ctx.font = '9px "DM Mono", monospace';
    ctx.textAlign = 'right';
    for (let i = 0; i <= gridLines; i++) {
      const y = padding.top + (chartH / gridLines) * i;
      const val = Math.round(maxCount * (1 - i / gridLines));
      ctx.fillText(String(val), padding.left - 4, y + 3);
    }

    // Draw lines for each exchange
    for (const exchange of exchanges) {
      const points = tradeRates[exchange];
      if (points.length < 2) continue;

      const color = EXCHANGE_COLORS[exchange] || '#888';
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = 0.8;
      ctx.beginPath();

      for (let i = 0; i < points.length; i++) {
        const x = padding.left + (i / (maxPoints - 1 || 1)) * chartW;
        const y = padding.top + chartH - (points[i].count / maxCount) * chartH;
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // X-axis: show a few time labels
    if (maxPoints > 0) {
      ctx.fillStyle = '#444';
      ctx.font = '9px "DM Mono", monospace';
      ctx.textAlign = 'center';
      const firstEx = exchanges[0];
      const pts = tradeRates[firstEx];
      const labelCount = Math.min(4, pts.length);
      for (let i = 0; i < labelCount; i++) {
        const idx = Math.round((i / (labelCount - 1 || 1)) * (pts.length - 1));
        const mk = pts[idx].minute;
        // Extract HH:MM from YYYYMMDDHHMM
        const hh = mk.slice(8, 10);
        const mm = mk.slice(10, 12);
        const x = padding.left + (idx / (maxPoints - 1 || 1)) * chartW;
        ctx.fillText(`${hh}:${mm}`, x, h - 4);
      }
    }
  }, [tradeRates, t]);

  useEffect(() => {
    drawChart();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => drawChart());
    observer.observe(canvas.parentElement);
    return () => observer.disconnect();
  }, [drawChart]);

  // Build L/S ratio pills from aggregated data
  const lsData = {};
  if (tradeRates) {
    for (const [exchange, points] of Object.entries(tradeRates)) {
      let totalLong = 0, totalShort = 0;
      for (const p of points) {
        totalLong += p.long || 0;
        totalShort += p.short || 0;
      }
      const total = totalLong + totalShort;
      if (total > 0) {
        lsData[exchange] = {
          longPct: Math.round((totalLong / total) * 100),
          shortPct: Math.round((totalShort / total) * 100),
        };
      }
    }
  }

  const exchanges = tradeRates ? Object.keys(tradeRates) : [];

  return (
    <div className="stats-panel stats-tpm-panel">
      <div className="stats-panel-header">
        <span className="stats-panel-title">{t('stats.tradesPerMin')}</span>
      </div>

      <div className="stats-tpm-chart-wrap">
        <canvas ref={canvasRef} className="stats-tpm-canvas" />
      </div>

      {exchanges.length > 0 && (
        <div className="stats-tpm-legend">
          {exchanges.map((ex) => (
            <div key={ex} className="stats-tpm-legend-item">
              <span
                className="stats-tpm-legend-dot"
                style={{ background: EXCHANGE_COLORS[ex] || '#888' }}
              />
              <span className="stats-tpm-legend-name">{formatExchangeLabel(ex)}</span>
              {lsData[ex] && (
                <span className="stats-tpm-ls-pills">
                  <span className="stats-tpm-ls-pill long">{lsData[ex].longPct}%L</span>
                  <span className="stats-tpm-ls-pill short">{lsData[ex].shortPct}%S</span>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { EXCHANGE_COLORS, formatExchangeLabel };
export default TradesPerMinPanel;
